// Public job detail page — the landing for Indeed (and the careers page).
//
// No PII, no internal data. Visitor can read the job, see pay + location,
// and apply via the hosted in-app form (pre-filled with this job).
//
// Phase 2 "The Look": token-driven + theme-aware, mobile-first, brand-driven.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CareersHeader, CareersFooter, brandVars } from './Careers';
import { usePublicBranding } from '../lib/publicBranding';
import { Card, Button, Icon } from '../components/ui';
import type { IconName } from '../components/ui';

interface PublicJob {
  id: string;
  job_code: string | null;
  title: string;
  profession: string | null;
  specialty: string | null;
  sub_specialty: string | null;
  description: string | null;
  city: string | null;
  state: string | null;
  pay_summary: string | null;
  hours_per_week: number | null;
  shift: string | null;
  duration_weeks: number | null;
  start_date: string | null;
  posted_at: string;
  updated_at: string;
}

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1';

export default function CareerJobDetail() {
  const { id } = useParams<{ id: string }>();
  const brand = usePublicBranding();
  const [job, setJob] = useState<PublicJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetch(`${API_BASE}/careers/jobs/${id}`)
      .then(r => {
        if (r.status === 404) throw new Error('not_found');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: { job: PublicJob }) => { if (!cancelled) setJob(data.job); })
      .catch((e: Error) => {
        if (!cancelled) setErr(e.message === 'not_found' ? 'This position is no longer open.' : 'Could not load this position.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // [phase4 track-D] JS head fallback. The primary SEO path is server-injected
  // (frontend/server.mjs prerenders <title>/OG/JSON-LD for crawlers). This is
  // the belt-and-suspenders copy for JS-executing crawlers (Googlebot) and
  // social link unfurls that hit the SPA directly: set document.title + the
  // OpenGraph/Twitter meta from the loaded job, and undo it on unmount.
  useEffect(() => {
    if (!job) return;
    const company = brand.company_name;
    const loc = [job.city, job.state].filter(Boolean).join(', ');
    const title = `${job.title}${company ? ` — ${company}` : ''}`;
    const description =
      (job.description?.replace(/\s+/g, ' ').trim().slice(0, 200)) ||
      [job.specialty, loc, job.pay_summary].filter(Boolean).join(' · ') ||
      `${job.title} position${loc ? ` in ${loc}` : ''}.`;
    const url = `${window.location.origin}/careers/${job.id}`;

    const prevTitle = document.title;
    document.title = title;

    const created: HTMLMetaElement[] = [];
    const setMeta = (attr: 'property' | 'name', key: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        el.setAttribute('data-seo-job', '1');
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    if (company) setMeta('property', 'og:site_name', company);
    if (brand.logo_url) setMeta('property', 'og:image', brand.logo_url);
    setMeta('name', 'twitter:card', brand.logo_url ? 'summary_large_image' : 'summary');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
    };
  }, [job, brand.company_name, brand.logo_url]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--sf2)', color: 'var(--t1)', ...brandVars(brand.primary_color) }}>
      <CareersHeader />

      <div style={{ maxWidth: 820, margin: '0 auto', padding: '24px 20px 80px' }}>
        <Link to="/careers" style={{ color: 'var(--t3)', textDecoration: 'none', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Icon name="chevron-left" size={14} /> All open positions
        </Link>

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--t3)' }}>Loading…</div>
        ) : err || !job ? (
          <Card style={{ marginTop: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: 'var(--t1)' }}>{err ?? 'Position not found'}</div>
            <Link to="/careers" style={{ color: 'var(--pr)', textDecoration: 'none', fontSize: 13 }}>← Back to open positions</Link>
          </Card>
        ) : (
          <JobContent job={job} />
        )}
      </div>

      <CareersFooter />
    </div>
  );
}

function JobContent({ job }: { job: PublicJob }) {
  const location = [job.city, job.state].filter(Boolean).join(', ');
  // Apply via the hosted in-app form, pre-filled with this job (creates a
  // candidate + links them to the job in the ATS).
  const applyUrl = `/apply?job_id=${encodeURIComponent(job.id)}&job_title=${encodeURIComponent(job.title)}${job.profession ? `&role=${encodeURIComponent(job.profession)}` : ''}`;

  return (
    <div style={{ marginTop: 20 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: 'clamp(22px, 4.5vw, 27px)', fontWeight: 800, letterSpacing: -0.3, color: 'var(--t1)' }}>
              {job.title}
            </h1>
            {job.specialty && (
              <div style={{ marginTop: 6, fontSize: 16, color: 'var(--t2)' }}>{job.specialty}{job.sub_specialty ? ` · ${job.sub_specialty}` : ''}</div>
            )}
          </div>
          {job.pay_summary && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--good-fg)' }}>{job.pay_summary}</div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 22 }}>
          {location && <FactPill icon="building" text={location} />}
          {job.profession && <FactPill icon="user" text={job.profession} />}
          {job.shift && <FactPill icon="clock" text={job.shift} />}
          {job.hours_per_week != null && <FactPill icon="pulse" text={`${job.hours_per_week} hrs/week`} />}
          {job.duration_weeks != null && <FactPill icon="calendar" text={`${job.duration_weeks}-week contract`} />}
          {job.start_date && <FactPill icon="calendar" text={`Start ${new Date(job.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`} />}
        </div>

        <Button href={applyUrl} size="lg" iconRight={<Icon name="arrow-right" size={16} />}>
          Apply now
        </Button>

        {job.description && (
          <>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--t1)', marginTop: 26, marginBottom: 10 }}>About the role</h2>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.65, color: 'var(--t2)' }}>
              {job.description}
            </div>
          </>
        )}

        <div style={{ marginTop: 32, padding: 18, background: 'var(--sf2)', border: '1px solid var(--bd)', borderRadius: 'var(--r-md)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)', marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon name="sparkles" size={15} /> How to apply
          </div>
          <div style={{ fontSize: 13, color: 'var(--t2)', lineHeight: 1.5 }}>
            Click <strong>Apply now</strong> to fill a quick form and attach your resume — it goes straight to our recruiters.
            A real human (not a bot) responds within 24 hours, weekdays.
          </div>
        </div>
      </Card>
    </div>
  );
}

function FactPill({ icon, text }: { icon: IconName; text: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 12px', background: 'var(--sf3)', borderRadius: 'var(--r-full)',
      fontSize: 13, color: 'var(--t2)', fontWeight: 500,
    }}>
      <Icon name={icon} size={14} /> {text}
    </span>
  );
}
